/**
 * Lightweight location-based currency display.
 *
 * IMPORTANT:
 * - This is display-only. Whop checkout is still charged in USD (plans are USD).
 * - We fetch an approximate FX rate client-side.
 */
import { useEffect, useMemo, useState } from 'react';

async function fetchJson(url, opts) {
  const res = await fetch(url, opts);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function detectCurrencyCode() {
  // ipapi is simple and doesn't require an API key for basic usage.
  // Fallback to USD if blocked.
  try {
    const data = await fetchJson('https://ipapi.co/json/', { method: 'GET' });
    const code = (data?.currency || '').toUpperCase();
    if (code && code.length === 3) return code;
  } catch {
    // ignore
  }
  return 'USD';
}

async function fetchUsdRates() {
  // Free, no-key exchange rate endpoint (best-effort).
  // If unavailable, we fallback to USD-only display.
  const data = await fetchJson('https://open.er-api.com/v6/latest/USD', { method: 'GET' });
  return data?.rates || null;
}

export function usePricingCurrency() {
  const [currency, setCurrency] = useState('USD');
  const [rate, setRate] = useState(1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const code = await detectCurrencyCode();
      if (cancelled) return;
      setCurrency(code);
      if (code === 'USD') {
        setRate(1);
        return;
      }
      try {
        const rates = await fetchUsdRates();
        if (cancelled) return;
        const r = rates?.[code];
        if (typeof r === 'number' && Number.isFinite(r) && r > 0) {
          setRate(r);
        } else {
          setCurrency('USD');
          setRate(1);
        }
      } catch {
        setCurrency('USD');
        setRate(1);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const formatter = useMemo(() => {
    return (amountUsd) => {
      const amount = currency === 'USD' ? amountUsd : amountUsd * rate;
      const abs = Math.abs(amount);
      const fractionDigits = abs >= 100 ? 0 : 2;
      const formatted = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: fractionDigits,
      }).format(amount);
      return {
        formatted: currency === 'USD' ? formatted : `≈ ${formatted}`,
        currency,
        isConverted: currency !== 'USD',
      };
    };
  }, [currency, rate]);

  return {
    currency,
    rate,
    formatUsd: formatter,
  };
}

